import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Container as BsContainer, Row, Col } from 'react-bootstrap';
import ApplicationLayout from 'v3/layouts/Application';
import Text from 'v3/components/Text';
import Button from 'v3/components/Button';
import { FaExpand, FaCompress } from 'react-icons/fa';
import apiReport from 'services/apis/report';
import TravelRow from './Collapse/TravelRow';
import TopFilter from './Filter';
import Box from './Service/box';
import AdvancedFilterModal from './Filter/AdvancedFilterModal';
import { StickyHeader, ListContainer, FilterBarWrap } from './styles';
import { COLS, GRID_TEMPLATE, GRID_GAP, MIN_TOTAL_PADDED } from './Service/gridConfig';
import Spinner from 'react-bootstrap/Spinner';
import moment from 'moment';
import { fetchTravelStatus } from 'utils/fetches';
import SortIcon from './shared/SortIcon';

export const TRAVEL_STATUS_DEFAULT = [
  'ST2',
  'RELEASED_COLLECTION',
  'ST8',
  'ENDED_LOADING',
  'ST9',
  'BLOCKED',
];

const ADV_KEYS = [
  "startDate",
  "endDate",
  "branch",
  "customers",
  "pcps",
  "negotiators",
  "statuses",
  "costCenters",
  "recuseCostCenters",
  "travelNumber",
  "travelId",
];

const LEGEND = [
  { key: 'legendTitle', label: 'Legenda:', isTitle: true },
  { key: 'inProgress', label: 'Em Andamento', color: '#2b19ca' },
  { key: 'critical', label: 'Reprovado', color: '#d32f2f' },
  { key: 'revalidation', label: 'Em Revalidação', color: '#fa8006' },
  { key: 'fail', label: 'Setor que criou o ajuste', color: '#ffd000ff' },
  { key: 'approved', label: 'Aprovadas', color: '#2e7d32' },
  { key: 'notStarted', label: 'Não iniciadas', color: '#90a4ae' },
  { key: 'hours', label: <span style={{ color: '#d32f2f' }}>Críticas &gt; 3h</span> },
];

const PAGE_SIZE_OPTIONS = [10, 20, 30, 50];
const LOCALSTORAGE_FILTER_KEY = 'travel-release-control-filter'

function fmtSec(seconds) {
  let total = Number(seconds || 0);
  if (!total) return '—';
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = Math.floor(total % 60);

  if (total < 3600) {
    return `${m}m ${s}s`;
  }
  return m ? `${h}h ${m}m` : `${h}h`;
}

function isFilled (v) {
  return (Array.isArray(v) && v.length > 0) ||
    (v && typeof v === 'object' && Object.keys(v).length > 0) ||
    (typeof v === 'string' && v.trim() !== '') ||
    (typeof v === 'number');
}

const COLORS = {
  green: '#2e7d32',
  gray: '#b0bec5',
  red: '#d32f2f',
  blue: '#2b19ca',
  orange: '#f57c00',
  yellow: '#ffd000',
};

function colorBySectorStatus(status) {
  switch ((status || '').toString().toUpperCase()) {
    case 'APPROVED': return COLORS.green;
    case 'REPROVED': return COLORS.red;
    case 'IN_PROGRESS': return COLORS.blue;
    case 'PENDING_ADJUSTMENT': return COLORS.orange;
    case 'CREATED_ADJUSTMENT': return COLORS.yellow;
    case 'NOT_STARTED':
    default: return COLORS.gray;
  }
}

function labelBySectorStatus(status) {
  switch ((status || '').toString().toUpperCase()) {
    case 'APPROVED': return 'Aprovado';
    case 'REPROVED': return 'Reprovado';
    case 'IN_PROGRESS': return 'Em andamento';
    case 'PENDING_ADJUSTMENT': return 'Pendente de ajuste';
    case 'CREATED_ADJUSTMENT': return 'Ajuste criado';
    case 'NOT_STARTED':
    default: return 'Não iniciado';
  }
}

const LOADER_COLOR = '#9aa4ae';

function InlineLoadingRow({ isFs }) {
  return (
    <div
      role="status"
      aria-busy="true"
      style={{
        width: '100%',
        border: '1px solid #e1e5eb',
        borderRadius: 12,
        background: '#eef2f6',
        padding: isFs ? '20px 24px' : '16px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
      }}
    >
      <div className="d-flex flex-row align-items-center" style={{ color: LOADER_COLOR }}>
        <Text type="body" style={{ color: LOADER_COLOR, fontWeight: 600 }}>
          Carregando
        </Text>
        <Spinner
          animation="border"
          role="status"
          className="ml-3"
          style={{ color: LOADER_COLOR }}
        />
      </div>
    </div>
  );
}

function toAdjustment(adjustment) {
  let color = 'warn';
  if (adjustment.status_slug === 'FINISHED') {
    color = 'ok';
  } else if (adjustment.status_slug === 'ANNULLED') {
    color = 'danger';
  }

  return {
    n: adjustment.id,
    duracao: fmtSec(adjustment.adjustment_timer),
    inicio: moment(adjustment.created_at).format('DD/MM/YYYY HH:mm'),
    fim: adjustment.finished_at ? moment(adjustment.finished_at).format('DD/MM/YYYY HH:mm') : '',
    tone: color,
    motivo: adjustment.reason || '',
    descricao: adjustment.reason_description || '',
    resolucao: adjustment.finished_description || '',
    origemSetor: adjustment?.from_group?.name || '',
    destinoSetor: adjustment?.to_group?.name || '',
    criadoPor: adjustment?.user_created?.name || '',
    resolvidoPor: adjustment?.user_updated?.name || '',
    statusSlug: adjustment?.status_slug,
  };
}

function normalizeToListShape(row) {
  const secs = row?.sectors || {};
  const temposPorSetor = [
    {
      label: 'Operação',
      time: fmtSec(secs?.operation?.time),
      color: colorBySectorStatus(secs?.operation?.status),
      hasPendency: secs?.operation?.status === 'PENDING_ADJUSTMENT',
      status: labelBySectorStatus(secs?.operation?.status),
      historico: secs?.operation?.adjustments?.map(toAdjustment) || [],
    },
    {
      label: 'Cadastro',
      time: fmtSec(secs?.register?.time),
      color: colorBySectorStatus(secs?.register?.status),
      hasPendency: secs?.register?.status === 'PENDING_ADJUSTMENT',
      status: labelBySectorStatus(secs?.register?.status),
      historico: secs?.register?.adjustments?.map(toAdjustment) || [],
    },
    {
      label: 'GRO',
      time: fmtSec(secs?.gro?.time),
      color: colorBySectorStatus(secs?.gro?.status),
      hasPendency: secs?.gro?.status === 'PENDING_ADJUSTMENT',
      status: labelBySectorStatus(secs?.gro?.status),
      historico: secs?.gro?.adjustments?.map(toAdjustment) || [],
    },
    {
      label: 'Check-list',
      time: fmtSec(secs?.checklist?.time),
      color: colorBySectorStatus(secs?.checklist?.status),
      hasPendency: secs?.checklist?.status === 'PENDING_ADJUSTMENT',
      status: labelBySectorStatus(secs?.checklist?.status),
      historico: secs?.checklist?.adjustments?.map(toAdjustment) || [],
    },
    {
      label: 'Negociação',
      time: fmtSec(secs?.negotiator?.time),
      color: colorBySectorStatus(secs?.negotiator?.status),
      hasPendency: secs?.negotiator?.status === 'PENDING_ADJUSTMENT',
      status: labelBySectorStatus(secs?.negotiator?.status),
      historico: secs?.negotiator?.adjustments?.map(toAdjustment) || [],
    },
    {
      label: 'Emissão',
      time: fmtSec(secs?.issuance?.time),
      color: colorBySectorStatus(secs?.issuance?.status),
      hasPendency: secs?.issuance?.status === 'PENDING_ADJUSTMENT',
      status: labelBySectorStatus(secs?.issuance?.status),
      historico: secs?.issuance?.adjustments?.map(toAdjustment) || [],
    },
  ];

  const headerDots = {
    operacao: temposPorSetor[0].status,
    cadastro: temposPorSetor[1].status,
    gro: temposPorSetor[2].status,
    checklist: temposPorSetor[3].status,
    negociacao: temposPorSetor[4].status,
    emissao: temposPorSetor[5].status,
  };

  const travelStatus = String(row.status || '').toUpperCase();
  const statusTag = (() => {
    if (Object.values(secs).some(s => String(s?.status).toUpperCase() === 'PENDING_ADJUSTMENT')) {
      return 'Em revalidação';
    }
    if (travelStatus === 'CANCELLED') return 'Cancelado';
    if (travelStatus === 'APPROVED') return 'Aprovada';
    if (travelStatus === 'IN_ANALYSIS') return 'Em andamento';
    return 'Não iniciada';
  })();
  return {
    id: row.id,
    status: travelStatus,
    header: {
      ...headerDots,
      statusTag,
      travelStatus,
      viagem: String(row.id),
      tempoAtividade: fmtSec(row.sector_timer),
      tempoFila: fmtSec(row.queue_timer),
      idadeViagem: fmtSec(row.travel_timer),
      cliente: row?.customer?.name || '-',
      ordemFila: (row.order ?? row.order_number ?? '').toString(),
      transportadora: row?.shipping_company?.name || '-',
      centroDeCusto: row?.cost_center?.name || '-',
      filial: row?.branch_office?.name || '-',
      highlight: false,
      veiculos: row?.vehicle_and_wagons?.join(', ') || '—',
      driverAndShipping: {
        transportadora: row?.shipping_company?.name || '—',
        motorista: row?.driver?.name || '—',
      },
      attendancesStatus: {
        name: row?.travel_status_name || '—',
      },
    },
    details: {
      temposPorSetor,
      validacoesSimultaneas: [],
      informacoesAdicionais: [
        { label: 'PCP:', value: row?.pcp?.name || '-' },
        { label: 'Negociadores:', value: (row?.negotiators || []).map(n => n.name).join(', ') || '-' },
        { label: 'GRO:', value: row?.gro_approver?.name || '-' },
        { label: 'Cadastro:', value: row?.register_approver?.name || '-' },
        { label: 'Negociação:', value: row?.negotiator_approver?.name || '-' },
        { label: 'Emissor:', value: row?.issuer?.name || '-' },
      ],
    },
  };
}

function makePageList(current, totalPages) {
  const pages = new Set([1, totalPages, current, current - 1, current + 1]);
  const arr = [...pages].filter(p => p >= 1 && p <= totalPages).sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < arr.length; i++) {
    out.push(arr[i]);
    if (i < arr.length - 1 && arr[i + 1] - arr[i] > 1) out.push('…');
  }
  return out;
}

function PaginationBar({
  page,
  totalPages,
  total,
  start,
  end,
  pageSize,
  onChangePage,
  onChangePageSize,
  loading,
}) {
  const pages = makePageList(page, totalPages);
  return (
    <div
      style={{
        display: 'flex',
        gap: 12,
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 8px 16px',
      }}
    >
      <div
        style={{ fontSize: 'var(--fs-sm, 12px)', color: '#607d8b', minHeight: 20 }}
        aria-live="polite"
      >
        {loading ? (
          <span className="d-inline-flex align-items-center" style={{ gap: 8 }}>
            Carregando
            <Spinner animation="border" size="sm" />
          </span>
        ) : (
          <>Exibindo {total === 0 ? 0 : start + 1}–{end} de {total} registros</>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 'var(--fs-sm, 12px)', color: '#607d8b', whiteSpace: 'nowrap' }}>
            Itens por página:
          </span>
          <select
            value={pageSize}
            onChange={e => onChangePageSize(Number(e.target.value))}
            disabled={loading}
            style={{
              height: 35,
              lineHeight: '30px',
              minWidth: 70,
              padding: '0 10px',
              fontSize: 'var(--fs-sm, 12px)',
              border: '1px solid #e1e5eb',
              borderRadius: 6,
              background: '#fff',
              verticalAlign: 'middle',
            }}
          >
            {PAGE_SIZE_OPTIONS.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <Button
            size="sm"
            variant="secondary"
            disabled={loading || page <= 1}
            onClick={() => onChangePage(page - 1)}
          >
            Anterior
          </Button>

          {pages.map((p, idx) =>
            p === '…' ? (
              <span key={`dots-${idx}`} style={{ padding: '0 4px', color: '#90a4ae' }}>…</span>
            ) : (
              <button
                key={p}
                onClick={() => onChangePage(p)}
                disabled={loading || p === page}
                style={{
                  minWidth: 30,
                  height: 30,
                  padding: '0 8px',
                  borderRadius: 6,
                  border: '1px solid #e1e5eb',
                  background: p === page ? '#e8f0fe' : '#fff',
                  fontSize: 'var(--fs-sm, 12px)',
                  cursor: (loading || p === page) ? 'default' : 'pointer',
                  opacity: loading ? 0.6 : 1,
                }}
              >
                {p}
              </button>
            )
          )}
          <Button
            size="sm"
            variant="secondary"
            disabled={loading || page >= totalPages}
            onClick={() => onChangePage(page + 1)}
          >
            Próxima
          </Button>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ isFs }) {
  return (
    <div
      role="status"
      style={{
        padding: isFs ? 24 : 16,
        textAlign: 'center',
        color: '#78909c',
      }}
    >
      Nenhum registro encontrado.
    </div>
  );
}

let interval = null;

export default function TravelReleaseControl() {
  const [filter, setFilter] = useState({
    startDate: null,
    endDate: null,
    sectorSlug: null,
    sectorStatus: null,
    travelIndicatorStatus: null,
    branch: null,
    customers: [],
    pcps: [],
    negotiators: [],
    statuses: [],
    costCenters: [],
    recuseCostCenters: [],
    travelNumber: null,
    travelId: null,
    page: 1,
    perPage: 20,
    sortColumn: null,
    sortDirection: 'none'
  })
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, perPage: 20, lastPage: 1 });
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const fsRootRef = useRef(null);
  const [isFs, setIsFs] = useState(false);
  const [statuses, setStatuses] = useState([]);

  function toggleSortStatus() {
    let direction = 'none';

    if (filter?.sortColumn !== 'status') {
      direction = 'ASC';
    } else {
      if (filter?.sortDirection === 'none') direction = 'ASC';
      else if (filter?.sortDirection === 'ASC') direction = 'DESC';
      else if (filter?.sortDirection === 'DESC') direction = 'none';
    }

    onSubmitFilters({
      ...filter,
      sortColumn: direction === 'none' ? null : 'status',
      sortDirection: direction
    }, 1);
  }

  useEffect(() => {
    const onChange = () => setIsFs(!!(document.fullscreenElement || document.webkitFullscreenElement));
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
    };
  }, []);

  const enterFullscreen = async () => {
    const el = fsRootRef.current || document.documentElement;
    if (el.requestFullscreen) await el.requestFullscreen();
    else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
  };
  const exitFullscreen = async () => {
    if (document.exitFullscreen) await document.exitFullscreen();
    else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
  };
  const toggleFullscreen = () => (isFs ? exitFullscreen() : enterFullscreen());

  const advancedBadgeCount = useMemo(() => {
    let n = 0;
    for (const k of ADV_KEYS) {
      if (k === 'startDate' || k === 'endDate') continue;
      if (isFilled(filter[k])) n += 1;
    }

    if (isFilled(filter.startDate)) {
      n += 1;
    }

    if (isFilled(filter.endDate)) {
      n += 1;
    }

    return n;
  }, [filter]);

  function mapCountsFromApi(c) {
    if (!c) return null;
    return {
      critical: Number(c.critical ?? 0),
      revalidation: Number(c.revalidation ?? 0),
      approved: Number(c.approved ?? 0),
      inProgress: Number(c.in_analysis ?? 0),
      notStarted: Number(c.not_started ?? 0),
    };
  }

  async function fetchList(params = {}) {
    setLoading(true);
    setApiError(null);
    try {
      const res = await apiReport.get('report-travel-validation', {
        params: {
          id: params.travelId,
          startDate: params.startDate,
          endDate: params.endDate,
          page: params.page,
          perPage: params.perPage,
          costCenterIds: params.costCenters ? params.costCenters.map(c => c.value) : [],
          recuseCostCenterIds: params.recuseCostCenters ? params.recuseCostCenters.map(c => c.value) : [],
          sectorSlug: params.sectorSlug,
          sectorStatus: params.sectorStatus,
          travelIndicatorStatus: params.travelIndicatorStatus,
          travelStatusIds: params.statuses ? params.statuses.map(s => s.id) : [],
          travelNumber: params.travelNumber,
          branchOfficeIds: params.branch ? [params.branch.value] : [],
          customerIds: params.customers ? params.customers.map(c => c.value) : [],
          negotiatorIds: params.negotiators ? params.negotiators.map(n => n.value) : [],
          pcpIds: params.pcps ? params.pcps.map(p => p.value) : [],
          page: params.page,
          perPage: params.perPage,
          sortColumn: params.sortColumn,
          sortDirection: params.sortDirection,
        },
      });

      const payload = res?.data ?? {};
      const list = Array.isArray(payload?.data) ? payload.data : [];
      const normalized = list.map(normalizeToListShape);

      const total = Number(payload?.total);
      const perPage = Number(payload?.per_page);
      const current = Number(payload?.current_page);
      const lastPage = Number(payload?.last_page);

      setItems(normalized);
      setCounts(mapCountsFromApi(payload?.counts));
      setPagination({ total, page: current, perPage, lastPage });
    } catch (err) {
      setApiError(err);
    } finally {
      setLoading(false);
    }
  }

  async function setDefaultTravelStatuses() {
    try {
      const statuses = await fetchTravelStatus();
      setStatuses(statuses);
      const json = localStorage.getItem(LOCALSTORAGE_FILTER_KEY);
      let newFilters = {
        ...filter,
        statuses: statuses.filter(s => TRAVEL_STATUS_DEFAULT.includes(s.code)),
      };

      if (json) {
        newFilters = JSON.parse(json);
      }

      updateFilter(newFilters);
      fetchList(newFilters);
    } catch (err) {
      //
    }
  }

  function updateFilter(newFilters) {
    const json = JSON.stringify(newFilters)
    localStorage.setItem(LOCALSTORAGE_FILTER_KEY, json);
    setFilter(newFilters);
  }

  function onSubmitFilters(filters = {}, page = 1) {
    const newFilter = {
      ...filters,
      page,
    };
    updateFilter(newFilter);
    fetchList(newFilter);
  }

  useEffect(() => {
    setDefaultTravelStatuses();
  }, [])

  useEffect(() => {
    if (!isFs) {
      clearInterval(interval);
      return;
    }

    interval = setInterval(() => {
      fetchList(filter);
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [isFs, filter]);

  const start = (pagination.page - 1) * pagination.perPage;
  const end = Math.min(pagination.page * pagination.perPage, pagination.total);

  return (
    <ApplicationLayout
      title="Controle de liberação de viagens"
      isOld
      onlyCenterComponent={false}
      contentFluid
      RightComponent={
        <Button variant="secondary" onClick={() => setShowAdvanced(true)}>
          <Text weight={500} color="dark" type="regular">
            {advancedBadgeCount > 0
              ? `Filtro avançado (${advancedBadgeCount})`
              : 'Filtro avançado'}
          </Text>
        </Button>
      }
    >
      {showAdvanced && (
        <AdvancedFilterModal
          show={showAdvanced}
          statuses={statuses}
          filter={filter}
          onClose={() => setShowAdvanced(false)}
          onApply={onSubmitFilters}
        />
      )}
      <FilterBarWrap>
        <BsContainer className="px-0">
          <TopFilter
            defaultPeriod=""
            filter={filter}
            onFilter={(data) => {
              updateFilter({
                ...data,
                page: 1,
              })
            }}
            onSubmit={onSubmitFilters}
            resultsTotal={pagination.total}
            loadingResults={loading}
          />
        </BsContainer>
      </FilterBarWrap>

      <div
        ref={fsRootRef}
        className="tv-fs-root"
        style={{
          fontSize: isFs ? '18px' : undefined,
          lineHeight: isFs ? 1.35 : undefined,
          ['--fs-sm']: isFs ? '14px' : '12px',
          ['--fs-md']: isFs ? '16px' : '14px',
          ['--fs-lg']: isFs ? '16px' : '14px',
          display: isFs ? 'flex' : undefined,
          flexDirection: isFs ? 'column' : undefined,
          minHeight: isFs ? '100vh' : undefined,
          background: isFs ? '#fff' : undefined,
          width: isFs ? '100vw' : undefined,
          margin: isFs ? 0 : undefined,
          padding: isFs ? '16px 20px' : undefined,
          borderRadius: isFs ? 0 : undefined,
          overflow: isFs ? 'hidden' : undefined,
          boxShadow: isFs ? 'none' : undefined,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <Box
            asLegend
            cards={LEGEND}
            className="mb-2 mt-4 justify-end"
            legendFontSize={isFs ? 16 : 14}
            legendDot={isFs ? 16 : 14}
            legendGap={isFs ? 16 : 14}
          />

          <Button
            variant="secondary"
            onClick={toggleFullscreen}
            title={isFs ? 'Sair da tela cheia' : 'Tela cheia'}
            aria-label={isFs ? 'Sair da tela cheia' : 'Tela cheia'}
            style={{
              height: 36,
              width: 36,
              padding: 0,
              display: 'grid',
              placeItems: 'center',
              borderRadius: 8,
              whiteSpace: 'nowrap',
              alignSelf: 'flex-start',
            }}
            className="mb-2 mt-4"
          >
            {isFs ? <FaCompress /> : <FaExpand />}
          </Button>
        </div>

        <Box
          className="mb-2"
          data={items}
          statusKeyProp="statusKey"
          dense
          minCard={isFs ? 280 : 240}
          gap={isFs ? 14 : 12}
          counts={counts}
          loading={loading}
        />

        <ListContainer $fs={isFs}>
          <StickyHeader aria-hidden="true">
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: GRID_TEMPLATE,
                columnGap: GRID_GAP,
                alignItems: 'center',
                boxSizing: 'border-box',
                padding: '0 16px',
                color: '#607d8b',
                fontSize: isFs ? 'var(--fs-lg, 20px)' : 14,
                fontWeight: 700,
                marginTop: '8px',
                marginBottom: '6px',
                whiteSpace: 'normal',
                overflowWrap: 'normal',
                wordBreak: 'normal',
                hyphens: 'none',
                minWidth: MIN_TOTAL_PADDED,
                width: `max(100%, ${MIN_TOTAL_PADDED}px)`,
              }}
            >
              {COLS.map(c => {
                const isStatus = c.key === 'status';
                return (
                  <div
                    key={c.key}
                    onClick={isStatus ? toggleSortStatus : undefined}
                    style={{
                      cursor: isStatus ? 'pointer' : 'default',
                      userSelect: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 6,
                      fontWeight: 700,
                      paddingLeft: 6,
                      paddingRight: 6,
                    }}
                  >
                    {c.label}

                    {isStatus && (
                      <SortIcon direction={filter?.sortDirection} loading={loading} />
                    )}
                  </div>
                );
              })}
            </div>
          </StickyHeader>
          <div
            className="mt-2"
            style={{
              minWidth: MIN_TOTAL_PADDED,
              width: `max(100%, ${MIN_TOTAL_PADDED}px)`,
              fontSize: isFs ? 'var(--fs-md, 18px)' : undefined,
            }}
          >
            {apiError ? (
              <div
                role="alert"
                style={{
                  padding: 8,
                  color: '#c62828',
                  textAlign: 'center',
                  borderRadius: 8,
                  fontWeight: 600,
                }}
              >
                {typeof apiError === 'string'
                  ? apiError
                  : apiError?.response?.data?.message ||
                  apiError?.response?.data?.error ||
                  (Array.isArray(apiError?.response?.data?.errors)
                    ? apiError.response.data.errors
                      .map(e => (typeof e === 'string' ? e : e?.message || ''))
                      .filter(Boolean)
                      .join(' • ')
                    : null) ||
                  'Erro ao carregar dados'}
              </div>
            ) : loading ? (
              <div
                style={{
                  minWidth: MIN_TOTAL_PADDED,
                  width: `max(100%, ${MIN_TOTAL_PADDED}px)`,
                  padding: isFs ? '12px 0' : '8px 0',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <InlineLoadingRow isFs={isFs} />
              </div>
            ) : items.length === 0 ? (
              <EmptyState isFs={isFs} />
            ) : (
              <Row className="g-0 px-0">
                {items.map(item => (
                  <Col key={item.id} md={12} className="px-0">
                    <TravelRow item={item} isFs={isFs} />
                  </Col>
                ))}
              </Row>
            )}
          </div>
        </ListContainer>

        <PaginationBar
          page={pagination.page}
          totalPages={Math.max(1, pagination.lastPage)}
          total={pagination.total}
          start={start}
          end={end}
          pageSize={filter?.pageSize}
          onChangePage={(p) =>
            onSubmitFilters({ ...filter }, p)
          }
          onChangePageSize={(ps) =>
            onSubmitFilters({ ...filter, perPage: ps }, 1)
          }
          loading={loading}
        />
      </div>
    </ApplicationLayout>
  );
}
